/**
 * Intake forms: the vocabulary the API and both clients share.
 *
 * Kept free of runtime dependencies, exactly as `domain.ts` and
 * `ordering.ts` are, so the server, the admin console and the public
 * booking bundle all describe a form with the same words.
 *
 * WHAT A FORM IS, AND WHAT IT IS NOT. A form is a document a business
 * asks a customer to fill in, acknowledge or sign: an intake sheet, a
 * cancellation policy, a colour consent, a release. It is versioned, so
 * editing it never rewrites what somebody already answered.
 *
 * It is NOT `service_questions` — the two or three boxes a service asks
 * at the moment of booking, which are untouched and still work. A
 * business may use both; neither reads the other.
 *
 * NEUTRAL WORDING IS A RULE, NOT A STYLE. What this product captures is
 * an ELECTRONIC ACKNOWLEDGMENT and an ELECTRONIC SIGNATURE. Nothing here
 * claims a legal guarantee, and no starter template is legal, medical or
 * regulatory advice.
 */

/* ------------------------------------------------------------------ */
/* Field types                                                         */
/* ------------------------------------------------------------------ */

/** Phase 1's field types, in the order the builder offers them. */
export const FIELD_KINDS = [
  'short_text',
  'long_text',
  'email',
  'phone',
  'address',
  'date',
  'time',
  'yes_no',
  'single_choice',
  'multi_choice',
  'acknowledgment',
  'heading',
  'info',
  'initials',
  'signature',
  'file',
] as const;
export type FieldKind = (typeof FIELD_KINDS)[number];

export const FIELD_KIND_LABELS: Record<FieldKind, string> = {
  short_text: 'Short text',
  long_text: 'Long text',
  email: 'Email',
  phone: 'Phone number',
  address: 'Address',
  date: 'Date',
  time: 'Time',
  yes_no: 'Yes or no',
  single_choice: 'Single choice',
  multi_choice: 'Multiple choice',
  acknowledgment: 'Checkbox acknowledgment',
  heading: 'Section heading',
  info: 'Informational text',
  initials: 'Initials',
  signature: 'Electronic signature',
  file: 'File or image upload',
};

/** A one-line explanation for the picker, in the words an owner uses. */
export const FIELD_KIND_HINTS: Record<FieldKind, string> = {
  short_text: 'One line: a name, a referral, a preference.',
  long_text: 'A paragraph: history, notes, what they would like.',
  email: 'An email address, checked for shape.',
  phone: 'A phone number.',
  address: 'A street address, over several lines.',
  date: 'A calendar date.',
  time: 'A time of day.',
  yes_no: 'Yes or no.',
  single_choice: 'One of a list of options.',
  multi_choice: 'Any number of a list of options.',
  acknowledgment: 'A statement they tick to say they have read it.',
  heading: 'A title between groups of questions. Asks nothing.',
  info: 'A paragraph of explanation. Asks nothing.',
  initials: 'They type their initials beside a statement.',
  signature: 'They type their full name as an electronic signature.',
  file: 'They attach a photo or a document. Stored privately.',
};

/** Kinds that display something and collect nothing. */
export const DISPLAY_KINDS: readonly FieldKind[] = ['heading', 'info'];

/** Kinds that record an agreement rather than an answer. */
export const SIGNATURE_KINDS: readonly FieldKind[] = ['acknowledgment', 'initials', 'signature'];

/** Kinds that carry a list of options, and are meaningless without one. */
export const CHOICE_KINDS: readonly FieldKind[] = ['single_choice', 'multi_choice'];

export function isDisplayKind(kind: FieldKind): boolean {
  return DISPLAY_KINDS.includes(kind);
}
export function isSignatureKind(kind: FieldKind): boolean {
  return SIGNATURE_KINDS.includes(kind);
}
export function isChoiceKind(kind: FieldKind): boolean {
  return CHOICE_KINDS.includes(kind);
}

/** What `form_signatures.kind` is for a field of this kind. */
export function signatureKindOf(kind: FieldKind): 'signature' | 'initials' | 'acknowledgment' | null {
  return kind === 'signature' ? 'signature' : kind === 'initials' ? 'initials' : kind === 'acknowledgment' ? 'acknowledgment' : null;
}

/* ------------------------------------------------------------------ */
/* The form itself                                                     */
/* ------------------------------------------------------------------ */

export const FORM_STATUSES = ['draft', 'published', 'unpublished', 'archived'] as const;
export type FormStatus = (typeof FORM_STATUSES)[number];

export const FORM_STATUS_LABELS: Record<FormStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  unpublished: 'Unpublished',
  archived: 'Archived',
};

/**
 * 'service_request' (0053) is the intake behind a service request: the
 * published form of that type is what the public page and the team's
 * "New request" screen ask. Every other type is a document tied to an
 * appointment or a customer.
 */
export const FORM_TYPES = ['intake', 'consent', 'waiver', 'policy', 'questionnaire', 'release', 'other', 'service_request'] as const;
export type FormType = (typeof FORM_TYPES)[number];

export const FORM_TYPE_LABELS: Record<FormType, string> = {
  intake: 'Intake',
  consent: 'Consent',
  waiver: 'Waiver',
  policy: 'Policy acknowledgment',
  questionnaire: 'Questionnaire',
  release: 'Release',
  other: 'Other',
  service_request: 'Service request intake',
};

export const FORM_AUDIENCES = ['everyone', 'new', 'returning'] as const;
export type FormAudience = (typeof FORM_AUDIENCES)[number];

export const FORM_AUDIENCE_LABELS: Record<FormAudience, string> = {
  everyone: 'Everyone',
  new: 'New customers only',
  returning: 'Returning customers only',
};

export const FORM_TIMINGS = ['during_booking', 'after_booking', 'before_arrival', 'check_in'] as const;
export type FormTiming = (typeof FORM_TIMINGS)[number];

export const FORM_TIMING_LABELS: Record<FormTiming, string> = {
  during_booking: 'While booking',
  after_booking: 'Straight after booking',
  before_arrival: 'Before they arrive',
  check_in: 'At check in',
};

export const FORM_FREQUENCIES = ['once', 'every_appointment', 'renew'] as const;
export type FormFrequency = (typeof FORM_FREQUENCIES)[number];

export const FORM_FREQUENCY_LABELS: Record<FormFrequency, string> = {
  once: 'Once only',
  every_appointment: 'Every appointment',
  renew: 'Again after a period',
};

export const ASSIGNMENT_SCOPES = ['industry', 'service', 'provider', 'location'] as const;
export type AssignmentScope = (typeof ASSIGNMENT_SCOPES)[number];

/* ------------------------------------------------------------------ */
/* Status of one person's one form                                     */
/* ------------------------------------------------------------------ */

/**
 * The six words an appointment or a customer record uses. `needs_review`
 * is the business's own flag — a staff member marking that somebody
 * should look at an answer — and never something the customer sets.
 */
export const REQUEST_STATUSES = ['not_sent', 'sent', 'started', 'completed', 'expired', 'needs_review'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export const REQUEST_STATUS_LABELS: Record<RequestStatus, string> = {
  not_sent: 'Not sent',
  sent: 'Sent',
  started: 'Started',
  completed: 'Completed',
  expired: 'Expired',
  needs_review: 'Needs review',
};

/** How each status should read on a badge. */
export const REQUEST_STATUS_TONES: Record<RequestStatus, 'neutral' | 'warn' | 'good' | 'bad'> = {
  not_sent: 'neutral',
  sent: 'neutral',
  started: 'warn',
  completed: 'good',
  expired: 'bad',
  needs_review: 'warn',
};

/** How long a secure form link is good for, unless the business says otherwise. */
export const DEFAULT_LINK_DAYS = 30;
export const MAX_LINK_DAYS = 180;

/** What a customer may attach. Checked again, by content, on the server. */
export const ALLOWED_UPLOAD_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'] as const;
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/* ------------------------------------------------------------------ */
/* Validating one answer                                               */
/* ------------------------------------------------------------------ */

export interface FieldShape {
  id: string;
  kind: FieldKind;
  label: string;
  required: boolean;
  options: string[];
  config: Record<string, unknown>;
}

export type AnswerValue = string | string[] | boolean | null;

/**
 * What is wrong with this answer, or null.
 *
 * SHARED ON PURPOSE. The customer's browser calls it to put a message
 * beside the box, and the server calls it again inside the transaction
 * that writes the submission — so the two can never disagree about what
 * a valid answer is, and nothing is trusted because a browser said so.
 */
export function validateAnswer(field: FieldShape, value: AnswerValue): string | null {
  if (isDisplayKind(field.kind)) return null;

  const empty = value === null || value === undefined || value === ''
    || (Array.isArray(value) && value.length === 0)
    || (field.kind === 'acknowledgment' && value !== true);

  if (empty) {
    if (!field.required) return null;
    if (field.kind === 'acknowledgment') return 'Please tick this to continue.';
    if (field.kind === 'signature') return 'Please type your name to sign.';
    if (field.kind === 'initials') return 'Please type your initials.';
    if (field.kind === 'file') return 'Please attach a file.';
    return 'This is required.';
  }

  switch (field.kind) {
    case 'acknowledgment':
      return value === true ? null : 'Please tick this to continue.';
    case 'yes_no':
      return value === 'yes' || value === 'no' ? null : 'Please answer yes or no.';
    case 'single_choice':
      return typeof value === 'string' && field.options.includes(value) ? null : 'Please choose one of the options.';
    case 'multi_choice':
      if (!Array.isArray(value)) return 'Please choose from the options.';
      return value.every((v) => field.options.includes(v)) ? null : 'Please choose from the options.';
    case 'email':
      return typeof value === 'string' && /^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(value.trim())
        ? null : 'Please check this email address.';
    case 'phone':
      return typeof value === 'string' && value.replace(/\D/g, '').length >= 7
        ? null : 'Please check this phone number.';
    case 'date':
      return typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? null : 'Please give a date.';
    case 'time':
      return typeof value === 'string' && /^\d{2}:\d{2}$/.test(value) ? null : 'Please give a time.';
    case 'signature':
    case 'initials': {
      const text = String(value).trim();
      if (text.length < (field.kind === 'initials' ? 1 : 2)) return 'Please type your name to sign.';
      return text.length <= 200 ? null : 'Please keep this shorter.';
    }
    case 'long_text':
      return String(value).length <= 10000 ? null : 'Please keep this under 10,000 characters.';
    default:
      return String(value).length <= 2000 ? null : 'Please keep this under 2,000 characters.';
  }
}

/** Every complaint a set of answers draws, keyed by field id. */
export function validateAnswers(fields: readonly FieldShape[], values: Record<string, AnswerValue>): Record<string, string> {
  const issues: Record<string, string> = {};
  for (const field of fields) {
    const problem = validateAnswer(field, values[field.id] ?? null);
    if (problem) issues[field.id] = problem;
  }
  return issues;
}

/** How much of the form is done, for the progress line the customer sees. */
export function completionOf(fields: readonly FieldShape[], values: Record<string, AnswerValue>): { answered: number; total: number; percent: number } {
  const asked = fields.filter((f) => !isDisplayKind(f.kind));
  const answered = asked.filter((f) => !validateAnswer({ ...f, required: true }, values[f.id] ?? null)).length;
  const total = asked.length;
  return { answered, total, percent: total === 0 ? 100 : Math.round((answered / total) * 100) };
}

/** An answer as one readable line, for a printed form or a staff panel. */
export function answerText(kind: FieldKind, value: AnswerValue): string {
  if (value === null || value === undefined || value === '') return '—';
  if (kind === 'acknowledgment') return value === true || value === 'true' ? 'Acknowledged' : 'Not acknowledged';
  if (kind === 'yes_no') return value === 'yes' ? 'Yes' : value === 'no' ? 'No' : String(value);
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  return String(value);
}

/**
 * Whether a form this customer has already done is still good.
 *
 * `once` never goes stale; `every_appointment` is stale the moment a new
 * appointment exists; `renew` goes stale after its own number of days.
 */
export function submissionStillCurrent(
  frequency: FormFrequency,
  renewAfterDays: number | null,
  lastCompletedAt: Date | string | null,
  now: Date = new Date(),
): boolean {
  if (!lastCompletedAt) return false;
  if (frequency === 'every_appointment') return false;
  if (frequency === 'once') return true;
  const days = renewAfterDays ?? 0;
  if (days <= 0) return false;
  const last = new Date(lastCompletedAt).getTime();
  return now.getTime() - last < days * 86_400_000;
}

/**
 * The sentence under a renewal-eligible form, offered instead of asking
 * somebody to type it all again. Recording the confirmation is a
 * submission of its own (`kind = 'confirmation'`), never an edit of the
 * one being confirmed.
 */
export const CONFIRM_UNCHANGED_PROMPT =
  'Nothing has changed since I last completed this form.';

/** The privacy line shown under every customer form unless a business writes its own. */
export const DEFAULT_PRIVACY_NOTE =
  'Your answers are sent to this business and stored securely. They are used to prepare for your appointment and are not shared with anyone else by this booking service.';

/**
 * What a signature block says above it. Neutral by requirement: this
 * product does not claim a legal guarantee about an electronic
 * signature, and this sentence must not start doing so.
 */
export const SIGNATURE_NOTICE =
  'Typing your name below records an electronic signature: your name, the date and time, and the version of this form you were shown.';
